import { useEffect, useRef } from 'react';

type Mode = 'missing' | 'retry_failed' | 'next_pass';
type Summary = {
  latestPass?: { passId?: string | null } | null;
  allowedModes?: Mode[];
  recommendedMode?: Mode | null;
};

// Shared with the page/modal integration tests: defaults must never select paid
// repeat work, and refreshing a summary must preserve a valid explicit choice.
export function useProofreadPassSelection({
  bookId, version, visible, singleChapter, summary, mode, setMode, setChecked, invalidate,
}: {
  bookId: string;
  version: string;
  visible: boolean;
  singleChapter: boolean;
  summary: Summary | null;
  mode: Mode;
  setMode: (mode: Mode) => void;
  setChecked: (checked: boolean) => void;
  invalidate: () => void;
}) {
  const scope = JSON.stringify([bookId, version, singleChapter, summary?.latestPass?.passId || '']);
  const previousScope = useRef(scope);
  useEffect(() => {
    const changedScope = previousScope.current !== scope;
    previousScope.current = scope;
    const allowed = summary?.allowedModes || [];
    const recommended = summary?.recommendedMode;
    const recovery = recommended && recommended !== 'next_pass' && allowed.includes(recommended)
      ? recommended
      : allowed.includes('retry_failed') ? 'retry_failed' : allowed.includes('missing') ? 'missing' : null;
    if (changedScope || !visible) {
      if (mode === 'next_pass') setChecked(false);
      setMode(visible && !singleChapter ? recovery || 'missing' : 'missing');
      invalidate();
      return;
    }
    if (singleChapter || !summary) return;
    if (allowed.includes(mode)) return;
    setMode(recovery || 'missing');
    if (!recovery) setChecked(false);
    invalidate();
  }, [scope, visible, singleChapter, summary, mode, setMode, setChecked, invalidate]);
}
